import React, { useEffect } from "react";
import { ArrowRight, Check, MapPin } from "lucide-react";
import Header from "../components/Header";
import Contact from "../components/Contact";
import Footer from "../components/Footer";
import { Toaster } from "../components/ui/toaster";
import { estimateRent } from "../estimationData";

const EXAMPLES = [
  { type: "studio", label: "Studio", surface: 25 },
  { type: "t2", label: "2 pièces", surface: 42 },
  { type: "t3", label: "3 pièces", surface: 65 },
];

const euro = (n) => `${n.toLocaleString("fr-FR")} €`;

const RentTable = ({ code }) => (
  <div data-testid="quartier-rent-table" className="overflow-hidden rounded-2xl border border-[#e0d8c8] bg-[#FBF9F5]">
    {EXAMPLES.map((ex) => {
      const vide = estimateRent({ location: code, type: ex.type, condition: "correct", surface: ex.surface, furnished: false });
      const meuble = estimateRent({ location: code, type: ex.type, condition: "correct", surface: ex.surface, furnished: true });
      return (
        <div key={ex.type} className="grid grid-cols-3 items-center gap-3 border-b border-[#ece5d8] px-5 py-4 last:border-b-0">
          <div>
            <div className="text-[15px] font-semibold text-[#26241F]">{ex.label}</div>
            <div className="text-[13px] text-[#9a9488]">{ex.surface} m²</div>
          </div>
          <div className="text-[14px] text-[#403d35]">
            <span className="block text-[11px] uppercase tracking-wider text-[#9a9488]">Vide</span>
            {euro(vide.monthly)} · {vide.perM2} €/m²
          </div>
          <div className="text-[14px] text-[#403d35]">
            <span className="block text-[11px] uppercase tracking-wider text-[#9a9488]">Meublé</span>
            {euro(meuble.monthly)} · {meuble.perM2} €/m²
          </div>
        </div>
      );
    })}
  </div>
);

export default function Quartier({ q }) {
  useEffect(() => {
    if (!window.location.hash) window.scrollTo(0, 0);
  }, [q.slug]);

  return (
    <div className="min-h-screen bg-[#F4F0E9]" data-testid={`quartier-page-${q.code}`}>
      <Header />
      <main>
        <section className="relative overflow-hidden pt-[74px]">
          <img src={q.image} alt={`Immeuble parisien — ${q.name}`} className="absolute inset-0 h-full w-full object-cover" />
          <div className="absolute inset-0 bg-gradient-to-r from-black/75 via-black/50 to-black/20" />
          <div className="relative mx-auto max-w-[1320px] px-5 py-24 lg:px-8 lg:py-32">
            <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-white/25 bg-white/5 px-4 py-1.5 text-[12px] font-medium uppercase tracking-[0.18em] text-white/85 backdrop-blur-sm">
              <MapPin className="h-3.5 w-3.5" /> {q.name}
            </p>
            <h1 className="max-w-3xl font-serifDisplay text-[42px] leading-[1.05] text-white sm:text-[56px] lg:text-[68px]">
              Gestion locative {q.short}
            </h1>
            <p className="mt-5 max-w-xl text-[18px] font-semibold text-white">{q.tagline}</p>
            <div className="mt-8 flex flex-wrap gap-4">
              <a href="#contact" data-testid="quartier-contact-cta" className="group flex items-center gap-2 rounded-md bg-[#B08D57] px-7 py-4 text-[15px] font-semibold text-[#26241F] transition-colors hover:bg-[#c19d63]">
                Confier mon bien <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </a>
              <a href="/estimation-loyer" data-testid="quartier-estimation-cta" className="rounded-md border border-white/40 bg-white/5 px-7 py-4 text-[15px] font-semibold text-white backdrop-blur-sm transition-colors hover:bg-white/15">
                Estimer mon loyer
              </a>
            </div>
          </div>
        </section>

        <section className="py-20 lg:py-24">
          <div className="mx-auto grid max-w-[1320px] gap-12 px-5 lg:grid-cols-2 lg:gap-16 lg:px-8">
            <div>
              <p className="mb-3 flex items-center gap-3 text-[12px] font-semibold uppercase tracking-[0.2em] text-[#B08D57]">
                <span className="h-px w-8 bg-[#B08D57]" /> Le marché locatif
              </p>
              <h2 className="font-serifDisplay text-[30px] leading-tight text-[#26241F] lg:text-[40px]">Louer {q.dans}</h2>
              <p className="mt-5 text-[16px] leading-relaxed text-[#6e6a62]">{q.intro}</p>
              <p className="mt-4 text-[16px] leading-relaxed text-[#6e6a62]"><strong className="text-[#26241F]">Profil des locataires :</strong> {q.tenants}</p>
              <div className="mt-6 flex flex-wrap gap-2">
                {q.neighborhoods.map((n) => (
                  <span key={n} className="rounded-full border border-[#d8cfbd] bg-[#FBF9F5] px-4 py-1.5 text-[13px] text-[#403d35]">{n}</span>
                ))}
              </div>
            </div>
            <div>
              <h3 className="text-[19px] font-semibold text-[#26241F]">Loyers moyens indicatifs — {q.name}</h3>
              <p className="mb-5 mt-2 text-[14px] text-[#6e6a62]">Ordres de grandeur hors charges, logement en état correct.</p>
              <RentTable code={q.code} />
              <p className="mt-3 text-[12px] text-[#9a9488]">
                Indicatif — ces montants ne constituent pas une estimation. Le loyer final dépend du bien{q.noEncadrement ? "" : " et des plafonds de l'encadrement des loyers"}.
              </p>
            </div>
          </div>
        </section>

        <section className="bg-[#EFEAE1] py-20 lg:py-24">
          <div className="mx-auto max-w-[1320px] px-5 lg:px-8">
            <h2 className="max-w-2xl font-serifDisplay text-[30px] leading-tight text-[#26241F] lg:text-[40px]">
              Pourquoi confier votre bien {q.du} à Loyaris ?
            </h2>
            <ul className="mt-10 grid gap-4 sm:grid-cols-2">
              {q.points.map((p) => (
                <li key={p} className="flex items-start gap-3 rounded-2xl border border-[#e0d8c8] bg-[#FBF9F5] p-5 text-[15px] text-[#403d35]">
                  <span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-[#26241F] text-white"><Check className="h-3 w-3" /></span>
                  {p}
                </li>
              ))}
            </ul>
            <div className="mt-10 grid gap-4 sm:grid-cols-3">
              {[["Formule Liberté", "3,60 %"], ["Formule Sérénité", "4,20 %"], ["Assurance loyers impayés", "2,10 %"]].map(([l, v]) => (
                <a key={l} href="/#tarifs" className="rounded-2xl bg-[#26241F] p-6 text-white transition-transform hover:-translate-y-1">
                  <div className="text-[12px] font-semibold uppercase tracking-[0.2em] text-[#B08D57]">{l}</div>
                  <div className="mt-2 font-serifDisplay text-[34px]">{v}</div>
                  <div className="text-[13px] text-white/60">TTC des loyers encaissés</div>
                </a>
              ))}
            </div>
          </div>
        </section>

        <Contact source={q.slug} />
        <Footer />
      </main>
      <Toaster />
    </div>
  );
}
